import { GroupPeopleType, TextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

import { isPersonScopeGroup } from './quote-scope.util';
import { effectiveQuoteLimit, type QuotesSettings } from '../quotes.dto';
import { SpreadTypeEnum } from '../spread.dto';

type Node = Record<string, unknown>;

/** Мерки рамки цитаты серии (R409/R430): их несёт рамка с `vignetteQuote`, считаны по эталону. */
interface QuoteFrameMarks {
  vignetteQuote?: boolean;
  maxFontSize?: number;
  quoteNominalCapacity?: number;
  /** Ёмкость рамки — предел обрезки (у карточек серии равна номиналу). */
  quoteFloorCapacity?: number;
  naturalAligns?: { fontSize?: number };
}

const OPEN = '«';
const CLOSE = '»';
/** Кавычки «…» печатаются в рамке и занимают её ёмкость наравне с текстом. */
const MARKS_LENGTH = OPEN.length + CLOSE.length;

/**
 * Знаков текста в рамке серии с «…»: предел, который режет посадка, — его же предлагают «Лица» (`limitByPerson`).
 * `reserve` — знаки, дописанные после посадки (подпись строки классрука, `leadLineQuoteReserve`).
 */
export const vignetteQuoteTextCapacity = (frameCapacity: number, reserve = 0): number =>
  Math.max(0, frameCapacity - MARKS_LENGTH - reserve);

/** Висячий знак на месте обрезки («слово:», «слово —»): перед «»» и подписью он читается оборванным. */
const DANGLING_TAIL = /[\s:;,\u2014\u2013-]+$/u;

/** Последнее целое слово в пределах `capacity` знаков без висячего знака; первое слово длиннее предела режется по пределу. */
function cutAtWord(text: string, capacity: number): string {
  const head = text.slice(0, Math.max(0, capacity));
  const boundary = text.charAt(head.length);
  const lastSpace = head.search(/\s\S*$/);
  const word = boundary === '' || /\s/.test(boundary) || lastSpace <= 0 ? head : head.slice(0, lastSpace);
  return word.replace(DANGLING_TAIL, '');
}

const stripQuotes = (text: string): string =>
  text.startsWith(OPEN) && text.endsWith(CLOSE) && text.length >= 2 ? text.slice(1, -1).trim() : text;

/**
 * Посадка цитаты в рамку серии (R409/R410/R430), чистая и одна на редактор, гейтвей и печать.
 * Один кегль — номинал рамки (`maxFontSize`), ступени пола нет (решение 23.10). Предел — ёмкость САМОЙ рамки
 * (`quoteFloorCapacity`; карточки серии пишут её равной номиналу при текущей плотности): хранимый `limit`
 * снят с рамок мастера на момент пересчёта и после смены плотности устаревает, поэтому он — предел только
 * рамки без своей ёмкости. `reserve` — знаки, которые
 * допишут после посадки (подпись строки классрука), они входят в ёмкость вместе с кавычками «…». Длиннее предела — обрезка по
 * целому слову и `quoteCut: true` (только в памяти чтения). `autoFit` рамки остаётся страховкой рендера
 * от остатка переполнения. Текст печатается в «…»; пустая цитата остаётся пустой.
 */
export function fitVignetteQuote<B extends Node>(box: B, text: string, limit: number | undefined, reserve = 0): B {
  const marks = box as QuoteFrameMarks;
  // «…» — типографика карточки серии; рамка дизайнера печатает текст как есть, режется тем же законом.
  const quoted = marks.vignetteQuote === true;
  const body = quoted ? stripQuotes(text.trim()) : text.trim();
  const rest: Node = { ...box };
  delete rest['quoteCut'];
  if (body === '') return { ...rest, text: { ...(box['text'] as Node | undefined), text: '' } } as unknown as B;

  const bound = marks.quoteFloorCapacity ?? limit;
  const cap = bound === undefined ? undefined : quoted ? vignetteQuoteTextCapacity(bound, reserve) : Math.max(0, bound - reserve);
  const cut = cap !== undefined && body.length > cap;
  const fitted = cut ? cutAtWord(body, cap) : body;
  const fontSize = marks.maxFontSize;
  const naturalAligns = marks.naturalAligns;

  return {
    ...rest,
    text: { ...(box['text'] as Node | undefined), text: quoted ? `${OPEN}${fitted}${CLOSE}` : fitted },
    ...(fontSize !== undefined && naturalAligns && { naturalAligns: { ...naturalAligns, fontSize } }),
    ...(cut && { quoteCut: true }),
  } as unknown as B;
}

/** Предел длины цитаты человека: его хранимый потолок (`limitByPerson`, с полом), иначе ёмкость рамки на полу. */
export function vignetteQuoteLimit(
  quotes: QuotesSettings | null | undefined,
  personId: string | null | undefined,
): number | undefined {
  if (!quotes || !personId || quotes.limitByPerson?.[personId] === undefined) return undefined;
  return effectiveQuoteLimit(quotes, personId);
}

/**
 * Садится рамка цитаты серии (`vignetteQuote`) и рамка цитаты дизайнера на развороте виньетки (ячейка пака);
 * рамка цитаты владельца на личном развороте печатается целой.
 */
const isFitBox = (node: Node, vignetteSpread: boolean): boolean =>
  node['type'] === 'text' &&
  node['leadLine'] !== true &&
  (node['vignetteQuote'] === true || (vignetteSpread && node['textType'] === TextTypeEnum.PERSON_QUOTE));

type GroupParams = { personId?: string | null; peopleType?: string | null };

/** Человек ячейки: её `personId`, а рамка владельца без привязки — владелец альбома. */
function cellPersonOf(params: GroupParams, outer: string | null, ownerPersonId: string | null): string | null {
  if (!isPersonScopeGroup(params)) return outer;
  const ownerRole = params.peopleType === GroupPeopleType.OWNER || params.peopleType === GroupPeopleType.GROUP_WITH_OWNER;
  return params.personId ?? (ownerRole ? ownerPersonId : null);
}

const textOf = (node: Node): string => String((node['text'] as { text?: unknown } | undefined)?.text ?? '');

function mapFit(
  nodes: unknown,
  personId: string | null,
  quotes: QuotesSettings | null | undefined,
  ownerPersonId: string | null,
  vignetteSpread: boolean,
): unknown {
  if (!Array.isArray(nodes)) return nodes;
  let changed = false;
  const next = (nodes as Node[]).map(node => {
    let out = node;
    if (node['type'] === 'group') {
      const inner = cellPersonOf((node['params'] ?? {}) as GroupParams, personId, ownerPersonId);
      const children = mapFit(node['drawableObjects'], inner, quotes, ownerPersonId, vignetteSpread);
      if (children !== node['drawableObjects']) out = { ...node, drawableObjects: children };
    } else if (isFitBox(node, vignetteSpread)) {
      out = fitVignetteQuote(node, textOf(node), vignetteQuoteLimit(quotes, personId));
    }

    if (out !== node) changed = true;
    return out;
  });
  return changed ? next : nodes;
}

/**
 * Проход посадки по разворотам — сразу после гашения цитат выключенных групп. Строка классрука
 * (`leadLine`) садится в подстановке до подписи — здесь её не трогают.
 */
export function fitVignetteQuotes<S extends Node>(
  spread: S,
  quotes: QuotesSettings | null | undefined,
  ownerPersonId?: string | null,
): S {
  const collages = spread['collages'];
  if (!Array.isArray(collages)) return spread;
  const vignetteSpread = spread['spread_type'] === SpreadTypeEnum.VIGNETTE;
  let changed = false;
  const next = (collages as Node[]).map(collage => {
    const objects = mapFit(collage['drawableObjects'], null, quotes, ownerPersonId ?? null, vignetteSpread);
    if (objects === collage['drawableObjects']) return collage;
    changed = true;
    return { ...collage, drawableObjects: objects };
  });
  return changed ? ({ ...spread, collages: next } as S) : spread;
}

function collectCut(nodes: unknown, personId: string | null, ownerPersonId: string | null, out: Set<string>): void {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes as Node[]) {
    if (node['type'] === 'group') {
      const inner = cellPersonOf((node['params'] ?? {}) as GroupParams, personId, ownerPersonId);
      collectCut(node['drawableObjects'], inner, ownerPersonId, out);
    } else if (node['quoteCut'] === true && personId) {
      out.add(personId);
    }
  }
}

/**
 * Люди, чья цитата обрезана посадкой (`quoteCut` на заполненной модели — признак чтения, в хранимую
 * модель не пишется). Строка классрука вне ячейки отдаёт id ведущего через `leadPersonId`.
 */
export function vignetteQuoteOverflowIds(
  model: { cover?: unknown; spreads?: unknown } | null | undefined,
  leadPersonId?: string | null,
  ownerPersonId?: string | null,
): string[] {
  const ids = new Set<string>();
  const surfaces = [model?.cover, ...(Array.isArray(model?.spreads) ? model.spreads : [])];
  for (const surface of surfaces) {
    const collages = (surface as Node | undefined)?.['collages'];
    if (!Array.isArray(collages)) continue;
    for (const collage of collages as Node[]) {
      collectCut(collage['drawableObjects'], leadPersonId ?? null, ownerPersonId ?? null, ids);
    }
  }

  return [...ids];
}
